/**
 * ╔═══════════════╗
 * ║ !!IMPORTANT!! ║
 * ╚═══════════════╝
 * Please do not use statsig feature gating in this file.
 * Read https://hello.atlassian.net/wiki/spaces/JIE/pages/2858513117/Blockers+for+Statsig+in+the+issue-view
 * for why and to follow the fix.
 */
import { fg } from '@atlassian/jira-feature-gating';
import { appendOperationQuery } from '../../common/utils/append-operation-query.tsx';
import type { IssueViewFetchOperations } from '../../types.tsx';
import getCommentsQuery from './gira-fragments/comments/index.tsx';
import { getContentPanelsQuery } from './gira-fragments/content-panels/index.tsx';
import {
	getEcosystemQuery,
	getLegacyRightWebPanelsQuery,
} from './gira-fragments/ecosystem/index.tsx';
import {
	NUM_INITIAL_COMMENTS_TO_LOAD,
	NUM_INITIAL_WORKLOGS_TO_LOAD,
} from './gira-fragments/index.tsx';
import { getJiraSettingsQuery } from './gira-fragments/jira-settings/index.tsx';
import { getContainersByTypeQuery } from './gira-fragments/layout/index.tsx';
import getMediaContextQuery from './gira-fragments/media-context/index.tsx';
import getPermissionsQuery from './gira-fragments/permissions/index.tsx';
import getProject, { isProjectFieldRemoved } from './gira-fragments/project/index.tsx';
import { getRemoteLinksQuery } from './gira-fragments/remote-links/index.tsx';
import getWorklogsQuery from './gira-fragments/worklogs/index.tsx';

const CREATED_ORDER_DESCENDING = '-created';

export const ISSUE_VIEW_INTERACTIVE_QUERY = 'issueViewInteractiveQuery';

export const getGiraUrl = (operation?: IssueViewFetchOperations | string): string =>
	`/rest/gira/1/${appendOperationQuery(operation)}`;

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { getContentPanelsQuery } from './gira-fragments/content-panels/index.tsx';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { getEcosystemQuery } from './gira-fragments/ecosystem/index.tsx';

// JIV-19906: retrieve query string from URL -- only expected to be used by client side code
// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const getURLFromWindow = () => new URL(window.location.href);

export const isViewIssueFieldRemoved = () => {
	// TODO: migrate to m2 and m3 FG.
	return (
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('remove-issueviewinteractivequery-m1') &&
		fg('remove-issueviewinteractivequery-m2') &&
		fg('remove-issueviewinteractivequery-m3')
	);
};

export const viewIssueQuery = (url: URL = getURLFromWindow(), includeForgeQuery = true) => `
    viewIssue(issueKey: $issueKey) {
        ${!fg('remove-issueviewinteractivequery-m2') ? 'canCreateSubTask' : ''}
        ${!fg('remove-issueviewinteractivequery-m2') ? 'canExportIssue' : ''}
        ${
					/**
					 * THIS QUERY ONLY FETCHES TOTAL COUNT OF COMMENTS
					 * For all other comment related data, use AGG endpoints
					 * Note: When activity sorting feature is enabled, we do not
					 * want to fetch with sort order passed through; we should defer
					 * to the saved sort order in the backend to return the correct
					 * sort order
					 */
					!fg('remove-issueviewinteractivequery-m3')
						? getCommentsQuery({
								maxResults: NUM_INITIAL_COMMENTS_TO_LOAD,
								startAt: 0,
								orderBy: CREATED_ORDER_DESCENDING,
								commentAmount: Math.floor(NUM_INITIAL_COMMENTS_TO_LOAD),
								url,
							})
						: ''
				}
        ${
					/**
					 * Note: When activity sorting feature is enabled, we do not
					 * want to fetch with sort order passed through; we should defer
					 * to the saved sort order in the backend to return the correct
					 * sort order
					 */

					fg('remove-issueviewinteractivequery-m3')
						? ''
						: getWorklogsQuery({
								startAt: 0,
								worklogAmount: Math.floor(NUM_INITIAL_WORKLOGS_TO_LOAD),
								url,
							})
				}
        ${getEcosystemQuery(includeForgeQuery)}
        ${getContentPanelsQuery()}
        ${getLegacyRightWebPanelsQuery()}
        ${!fg('remove-issueviewinteractivequery-m1') ? 'totalAttachmentsCount' : ''}
        ${!fg('remove-issueviewinteractivequery-m2') ? getContainersByTypeQuery() : ''}
        ${getRemoteLinksQuery()}
    }
`;

const getForgeQuery = () => `
    forge {
        workItemPanels(
            cloudId: $cloudId,
            context: {
                workItemIdOrKey: $issueKey
            }
        ) {
            moduleId
            pinnedTo
            instances {
                id
                pinnedTo
                collapsed
                added
            }
        }
    }
`;

export const getJiraQueryEcosystemFirstSeenOnIssueView = () => `
    jira {
        ecosystemFirstSeenOnIssueView
    }
`;

export const getJiraQuery = (includeForgeQuery = false) =>
	fg('remove-issueviewinteractivequery-m2')
		? ''
		: `
    jira {
        ecosystemFirstSeenOnIssueView
        hasAdministerPermission: hasGlobalPermission(key: ADMINISTER)
        hasUserPickerPermission: hasGlobalPermission(key: USER_PICKER)
    	${includeForgeQuery ? getForgeQuery() : ''}
	}
`;

export const buildIssueQuery = (url?: URL) =>
	isIssueViewInteractiveQueryRemoved() // For now just making it return nothing to avoid errors - API call to be fully removed in JPX-597
		? `query issueViewInteractiveQueryToBeRemoved {
	__typename
}`
		: `
query ${ISSUE_VIEW_INTERACTIVE_QUERY}($issueKey: String!${isProjectFieldRemoved() ? '' : ', $projectKey: String'})  {
        ${!fg('remove-issueviewinteractivequery-m2') ? getJiraQuery() : ''}
        ${!fg('remove-issueviewinteractivequery-m2') ? getMediaContextQuery() : ''}
        ${!isViewIssueFieldRemoved() ? viewIssueQuery(url) : null}
        ${!fg('remove-issueviewinteractivequery-m1') ? getJiraSettingsQuery() : ''}
        ${!fg('remove-issueviewinteractivequery-m2') ? getPermissionsQuery() : ''}
        ${isProjectFieldRemoved() ? '' : getProject()}
    }

`;

export const buildIssueQueryV2 = (url?: URL) =>
	isIssueViewInteractiveQueryRemoved() // For now just making it return nothing to avoid errors - API call to be fully removed in JPX-597
		? `query issueViewInteractiveQueryToBeRemoved {
	__typename
}`
		: `
query ${ISSUE_VIEW_INTERACTIVE_QUERY}($issueKey: String!${isProjectFieldRemoved() ? '' : ', $projectKey: String'}${fg('remove-issueviewinteractivequery-m2') ? '' : ', $cloudId: ID'})  {
        ${!fg('remove-issueviewinteractivequery-m2') ? getJiraQuery(/* includeForgeQuery */ true) : ''}
        ${!fg('remove-issueviewinteractivequery-m2') ? getMediaContextQuery() : ''}
        ${!isViewIssueFieldRemoved() ? viewIssueQuery(url, /* includeForgeQuery */ false) : null}
        ${!fg('remove-issueviewinteractivequery-m1') ? getJiraSettingsQuery() : ''}
        ${!fg('remove-issueviewinteractivequery-m2') ? getPermissionsQuery() : ''}
        ${isProjectFieldRemoved() ? '' : getProject()}
    }
`;

// wrapper function for FG is used to enable easier remove of issueViewInteractiveQuery when all FG is rolled out:
// https://hello.atlassian.net/wiki/spaces/JFP/pages/6088836124/Investigate+Improving+issueViewInteractiveQuery+Measurement+And+Rollout
export const isIssueViewInteractiveQueryRemoved = () => {
	return (
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('remove-issueviewinteractivequery-m1') &&
		fg('remove-issueviewinteractivequery-m2') &&
		fg('remove-issueviewinteractivequery-m3')
	);
};

export const getGiraRequestOptions = (issueKey: string, projectKey: string, url?: URL) => {
	const payload = {
		operationName: ISSUE_VIEW_INTERACTIVE_QUERY,
		query: buildIssueQuery(url),
		variables: {
			issueKey,
			projectKey,
		},
	};

	return {
		method: 'POST',
		body: JSON.stringify(payload),
	};
};
